import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { formatEventDate, formatEventTime } from 'src/app/utils/date-format';
import { environment } from 'src/environments/environment';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { TranslateService } from '@ngx-translate/core';
import { RecentTicket } from 'src/app/models/core/recent-ticket.model';

@Component({
  selector: 'app-share-ticket-modal',
  templateUrl: './share-ticket-modal.component.html',
  styleUrls: ['./share-ticket-modal.component.scss'],
  standalone: false
})
export class ShareTicketModalComponent implements OnInit {
  isNative = false;
  @Input() ticket!: RecentTicket;

  constructor(
    private modalCtrl: ModalController,
    private readonly _translate: TranslateService,
    private customToast: CustomToastService,
  ) { }

  ngOnInit() {
    this.isNative = Capacitor.isNativePlatform();
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  messageQuota(): string {
    return (
      'Link habilitado para registrar ' +
      this.ticket.quota +
      ' asistente/s. ' +
      this.ticket.product.share_message
    );
  }

  get messageToShare(): string {
    const { product, event } = this.ticket;

    if (product.is_vip) {
      const lines = [
        `${this.ticket.fullname ?? 'Hola'}, te comparto el link con los QR de todos los invitados al evento ${event.name}.`,
        `Tus invitados tendrán acceso a ${product.name} que tendrá lugar el ${formatEventDate(event.initial_date)} a las ${formatEventTime(event.initial_date)}.`,
        `Accediendo a este link, cada uno de los invitados deberá descargar su QR y presentarlo en la puerta el día del evento.`,
      ];

      if (product.min_age > 0) {
        lines.push(
          `Te recuerdo que todos los invitados deben ser mayores de ${product.min_age} y deberán presentar DNI para ingresar.`
        );
      }

      return lines.join('\n');
    }

    if (product.min_age && product.min_age !== 0) {
      return `${this.messageQuota()}. Producto habilitado para mayores de ${product.min_age} años.`;
    }

    return product.share_message;
  }

  get ticketLink(): string {
    return `${environment.ecommerceDomain}/ticket-verification/${this.ticket?.uuid}`;
  }

  get fullShareMessage(): string {
    return `${this.messageToShare}\n\n${this.ticketLink}`;
  }

  copyMessageToClipboard(successKey: string = 'share-ticket-modal.copy-message-success') {
    navigator.clipboard.writeText(this.fullShareMessage).then(() => {
      this.customToast.show(this._translate.instant(successKey), 3000, 'success');
    }).catch(() => {
      this.customToast.show(this._translate.instant('share-ticket-modal.copy-message-error'), 3000, 'error');
    });
  }

  async copyToClipboard() {
    const message = this.messageToShare;
    const link = this.ticketLink;

    const htmlContent = `
      <p style="color: #000; white-space: pre-wrap;">${message}</p>
      <p><a href="${link}" target="_blank" style="color: #007aff; text-decoration: underline;">${link}</a></p>
    `;

    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlContent;
    document.body.appendChild(tempElement);

    const range = document.createRange();
    range.selectNode(tempElement);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    const copied = document.execCommand('copy');
    document.body.removeChild(tempElement);

    if (copied) {
      this.customToast.show(this._translate.instant('share-ticket-modal.copy-message-success'), 3000, 'success');
      this.closeModal();
    } else {
      this.customToast.show(this._translate.instant('share-ticket-modal.copy-message-error'), 3000, 'error');
    }
  }

  shareWhatsApp() {
    window.open(`https://wa.me?text=${encodeURIComponent(this.fullShareMessage)}`, '_blank');
    this.closeModal();
  }

  shareEmail() {
    const productName = this.ticket?.product.name ?? 'evento';
    const subject = this._translate.instant('share-ticket-modal.email-subject', { productName });
    const body = encodeURIComponent(this.fullShareMessage);
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;

    window.open(mailtoUrl, '_blank');
    this.closeModal();
  }

  shareInstagram() {
    this.copyMessageToClipboard('share-ticket-modal.instagram-copy-success');

    setTimeout(() => {
      window.open('https://www.instagram.com/accounts/login/?next=/', '_blank');
      this.closeModal();
    }, 2100);
  }

  async shareNative() {
    try {
      this.closeModal();
      await Share.share({
        title: 'Compartir entrada',
        text: this.fullShareMessage,
        dialogTitle: 'Compartir con...'
      });
    } catch (error) {
      console.error('Error al compartir:', error);
    }
  }

}


